import apiClient from './client';

export interface Quote {
  id: string;
  quote_number: string;
  job_id: string;
  job_title?: string;
  client_id?: number;
  client_name?: string;
  client_company?: string;
  client_email?: string;
  client_phone?: string;
  client_address?: string;
  status: string;
  total: number;
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  notes?: string;
  terms?: string;
  valid_until?: string;
  created_at: string;
  updated_at?: string;
}

export interface QuoteSection {
  id: string;
  quote_id: string;
  name: string;
  display_order: number;
  heading_level: number;
  subtotal: number;
}

export interface QuoteLineItem {
  id: string;
  section_id: string;
  item_name: string;
  description?: string;
  quantity: number;
  unit?: string;
  unit_price: number;
  total: number;
  display_order: number;
}

export interface QuoteDetail extends Quote {
  sections: QuoteSection[];
  items: QuoteLineItem[];
}

export interface QuotesListResponse {
  quotes: Quote[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const getQuotes = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  job_id?: string;
}): Promise<QuotesListResponse> => {
  const response = await apiClient.get('/quotes', { params });
  return response.data.data;
};

export const getQuotesByJob = async (jobId: string): Promise<Quote[]> => {
  const response = await apiClient.get('/quotes', { 
    params: { job_id: jobId, limit: 100 } 
  });
  return response.data.data.quotes;
};

export const getQuotesByClient = async (clientId: string): Promise<Quote[]> => {
  const response = await apiClient.get('/quotes', { 
    params: { client_id: clientId, limit: 100 } 
  });
  return response.data.data.quotes;
};

export const getQuote = async (id: string): Promise<QuoteDetail> => {
  const response = await apiClient.get(`/quotes/${id}`);
  return response.data.data;
};

export const createQuote = async (jobId: string): Promise<{ id: number; quoteNumber: string }> => {
  const response = await apiClient.post('/quotes', { jobId });
  return response.data.data;
};

export const updateQuote = async (id: string, data: { status?: string }): Promise<void> => {
  await apiClient.put(`/quotes/${id}`, data);
};

export const deleteQuote = async (id: string): Promise<void> => {
  await apiClient.delete(`/quotes/${id}`);
};

export const sendQuote = async (id: string, message?: string): Promise<{ emailed: boolean; emailTo?: string; emailReason?: string }> => {
  const response = await apiClient.post(`/quotes/${id}/send`, message ? { message } : {});
  return response.data || { emailed: false };
};

export const duplicateQuote = async (id: string): Promise<{ id: number; quoteNumber: string }> => {
  const response = await apiClient.post(`/quotes/${id}/duplicate`);
  return response.data.data;
};

export const createChangeOrder = async (id: string): Promise<{ id: number; quoteNumber: string }> => {
  const response = await apiClient.post(`/quotes/${id}/change-order`);
  return response.data.data;
};

export const getQuoteAudit = async (id: string): Promise<unknown[]> => {
  const response = await apiClient.get(`/quotes/${id}/audit`);
  return response.data.data;
};

export const createQuoteFromEstimate = async (
  estimateIds: number[], 
  labourEstimateIds: number[],
  jobId: number
): Promise<{ id: number; quoteNumber: string }> => {
  const response = await apiClient.post('/quotes/from-estimate', { estimateIds, labourEstimateIds, jobId });
  return response.data.data;
};
